import { DropdownAnchor } from 'components/Dropdown/DropdownAnchor';
import {
  EggSharing,
  GalleryBreakerShareDropdown,
  GribbleShareDropdown,
} from 'components/Sharing/ShareTargets';
import React from 'react';
import styles from './CollectionSidebar.module.scss';
import { ChevronIcon } from 'icons/chevron';
import cls from 'classnames';
// import { Switch } from 'components/Switch/Switch';
import { externalRoutes } from 'routing/externalRoutes';
import { useCollectionContext } from './context';
import {
  CollectionBreaker,
  CollectionEgg,
  CollectionEntities,
  CollectionEntityType,
  CollectionGribble,
  EntityMetadata,
} from './model';
import { Button } from '../Button/Button';
import { HorizontalDivider } from '../HorizontalDivider/HorizontalDivider';
import { Link } from 'react-router-dom';
import { CoinbaseNftIcon, LooksRareIcon, WarningIcon } from 'components/Icons/Icons';
import { TraitDisplayCard } from '../TraitCard/TraitDisplayCard';
import Skeleton from 'react-loading-skeleton';
import { useRandom } from '../../hooks/useRandom';
import { useCollection, useCollectionEntityMetadata } from './fetcher';
import { Attribute, traitAttributesTransformer } from 'components/TraitCard/transformer';
import { useEggBreakerPairs } from 'contexts/EggBreakerPairContext';
import { useUser } from 'hooks/useUser';
import { DebugHttpFailure } from '../DebugHttpFailure/DebugHttpFailure';
import OpenSeaIcon from 'components/Components/OpeanSea';
import { RenderingWarning } from './RenderingWarning';

const shareText = 'Share';

type ViewOnProps = {
  openSea?: string;
  looksRare?: string;
  coinbase?: string;
};
function ViewOn({ openSea, looksRare, coinbase }: ViewOnProps) {
  return (
    <>
      <Button xsmall id="collection-modal-dropdown" data-bs-toggle="dropdown">
        View On <ChevronIcon />
      </Button>

      <div
        className={cls('dropdown-menu', styles.dropdownViewOn)}
        aria-labelledby="collection-modal-dropdown"
      >
        <p className="overline-small text-60">View on</p>
        {openSea && (
          <a className={styles.dropdownViewOnItem} target="_new" href={openSea}>
            <OpenSeaIcon />
            <span className="body-default"> OpenSea </span>
          </a>
        )}

        {looksRare && (
          <a className={styles.dropdownViewOnItem} target="_new" href={looksRare}>
            <LooksRareIcon />
            <span className="body-default"> LooksRare </span>
          </a>
        )}

        {coinbase && (
          <a className={styles.dropdownViewOnItem} target="_new" href={coinbase}>
            <CoinbaseNftIcon />
            <span className="body-default"> CoinbaseNFT </span>
          </a>
        )}
      </div>
    </>
  );
}

function HeadingContainer({
  title,
  subtitle,
}: {
  title: React.ReactNode;
  subtitle: React.ReactNode;
}) {
  return (
    <div className={styles.headingContainer}>
      <p className="m-0 text-60 overline-small">{subtitle}</p>
      <p className="m-0 text-100 subheading-large">{title}</p>
    </div>
  );
}

type IndeterminateProperties =
  | { type: 'loading' }
  | { type: 'success'; data: Attribute[] }
  | { type: 'error'; message?: string };

type TraitsProps = {
  properties: IndeterminateProperties;
};

function TraitsBody({ properties }: TraitsProps) {
  if (properties.type === 'loading') {
    return <div className={styles.traitsGrid}>{placeholderTraits}</div>;
  } else if (properties.type === 'error') {
    return (
      <div>
        <p>💀 Something went wrong loading properties</p>
        <DebugHttpFailure message={properties.message} />
      </div>
    );
  } else {
    return (
      <div className={styles.traitsGrid}>
        {properties.data.map((properties) => {
          return <TraitDisplayCard {...properties} key={properties.name} small />;
        })}
      </div>
    );
  }
}

function Traits({ properties }: TraitsProps) {
  return (
    <div className={styles.traitsGridContainer}>
      {!properties ? (
        // Rendering the properties header feels a little out of place
        // in the initial skeleton loader so we use a skeleton instead
        <Skeleton width="25%" />
      ) : (
        <h2 className="m-0 text-60 overline-default">Properties</h2>
      )}
      <TraitsBody properties={properties} />
    </div>
  );
}

// Guaranteed to always have 6 traits + 5 colors = 11 placeholders
const placeholderTraits = Array.from({ length: 11 }, (_, i) => (
  <Skeleton key={i} width="100%" height="100%" containerClassName={styles.placeholderParent} />
));

const loadingImage = (
  <Skeleton
    width="100%"
    className={styles.fullWidthImage}
    containerClassName={styles.placeholderParent}
  />
);
type SidebarBreakerProps = {
  // Might not be loaded
  breaker?: CollectionBreaker;
  // Might not be loaded
  properties: IndeterminateProperties;
};

function SidebarBreaker({ breaker, properties }: SidebarBreakerProps) {
  const titleLength = useRandom(120, 200);
  return (
    <div className={styles.sidebar}>
      <HeadingContainer
        // Breakers currently don't have any metadata we can use as subtitle
        // so we're adding a <br /> to make sure the layout doesn't shift
        // when changing between breakers and other NFTs
        subtitle={<br />}
        title={breaker ? `DuskBreaker #${breaker.id}` : <Skeleton width={titleLength.current} />}
      />
      <div className={styles.imageButtonsSection}>
        <div className={styles.buttonsGrid}>
          {breaker ? (
            <>
              <DropdownAnchor>
                <GalleryBreakerShareDropdown
                  breaker={{ id: Number(breaker.id) }}
                  text={shareText}
                  align="left"
                />
              </DropdownAnchor>
              <ViewOn
                openSea={externalRoutes.viewLinks.openSea.duskbreakers(Number(breaker.id))}
                coinbase={externalRoutes.viewLinks.coinbaseNft.duskbreakers(Number(breaker.id))}
                looksRare={externalRoutes.viewLinks.looksRare.duskbreakers(Number(breaker.id))}
              />
            </>
          ) : (
            <>
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
            </>
          )}
        </div>
        {breaker ? (
          <div className={styles.image}>
            <img src={breaker.image.full} className={styles.fullWidthImage} alt="Gribble Egg" />
          </div>
        ) : (
          loadingImage
        )}
        <Traits properties={properties} />
      </div>
    </div>
  );
}

type SidebarEggProps = {
  egg?: CollectionEgg;
  setPairingModalOpen?: () => void;
  unpairEgg: () => void;
};
function SidebarEgg({ egg, setPairingModalOpen, unpairEgg }: SidebarEggProps) {
  const titleLength = useRandom(120, 200);
  const subTitleLength = useRandom(100, 120);

  return (
    <div className={styles.sidebar}>
      <HeadingContainer
        subtitle={egg ? `Level ${egg.level}` : <Skeleton width={subTitleLength.current} />}
        title={egg ? `Gribble Egg #${egg.id}` : <Skeleton width={titleLength.current} />}
      />
      <div className={styles.imageButtonsSection}>
        <div className={styles.buttonsGrid}>
          {egg ? (
            <>
              <Link to={'/hatchening?egg=' + egg.id}>
                <Button xsmall fullWidth primary disabled={egg.hatching}>
                  {egg.hatching ? 'Hatch in progress...' : 'Hatch Egg'}
                </Button>
              </Link>
              <Button
                xsmall
                fullWidth
                onClick={egg.pairedBreaker ? unpairEgg : setPairingModalOpen}
              >
                {egg.pairedBreaker ? 'Unpair Breaker' : 'Pair Breaker'}
              </Button>
            </>
          ) : (
            <>
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
            </>
          )}
        </div>
        {egg?.hatching && (
          <article className={styles.hatchingEggWarning}>
            <WarningIcon />
            <p className={cls('body-default', styles.warningText)}>
              Your eggs are getting hatched... but it&apos;s taking longer than normal. Please check
              back in a few hours
            </p>
          </article>
        )}
        <HorizontalDivider />
        <div className={styles.buttonsGrid}>
          {egg ? (
            <>
              <DropdownAnchor>
                <EggSharing egg={{ id: Number(egg.id) }} text={shareText} align="left" />
              </DropdownAnchor>
              <ViewOn
                openSea={externalRoutes.viewLinks.openSea.eggs(Number(egg.id))}
                coinbase={externalRoutes.viewLinks.coinbaseNft.eggs(Number(egg.id))}
                looksRare={externalRoutes.viewLinks.looksRare.eggs(Number(egg.id))}
              />
            </>
          ) : (
            <>
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
            </>
          )}
        </div>
        <div className={styles.image}>
          {egg && egg.pairedBreaker && (
            <img
              src={egg.pairedBreaker.breakerSrc}
              alt="Paired Breaker"
              className={styles.pairedBreaker}
            />
          )}
          {egg ? (
            <img src={egg.image.full} className={styles.fullWidthImage} alt="Gribble Egg" />
          ) : (
            loadingImage
          )}
        </div>
      </div>
    </div>
  );
}

export type SidebarGribbleProps = {
  gribble?: CollectionGribble;
  properties: IndeterminateProperties;
  setPairingModalOpen?: () => void;
  unpairGribble?: () => void;
};

function SidebarGribble({
  gribble,
  properties,
  setPairingModalOpen,
  unpairGribble,
}: SidebarGribbleProps) {
  const titleLength = useRandom(120, 200);
  const subtitleLength = useRandom(60, 120);

  return (
    <div className={styles.sidebar}>
      <HeadingContainer
        // TODO: createdAt doesn't exist on gribbles?
        subtitle={gribble ? `Egg #${gribble.id}` : <Skeleton width={subtitleLength.current} />}
        title={gribble ? `Gribble #${gribble.id}` : <Skeleton width={titleLength.current} />}
      />
      <div className={styles.imageButtonsSection}>
        {gribble ? (
          <>
            <Button
              xsmall
              fullWidth
              onClick={gribble.pairedBreaker ? unpairGribble : setPairingModalOpen}
            >
              {gribble.pairedBreaker ? 'Unpair Breaker' : 'Pair Breaker'}
            </Button>
            <HorizontalDivider />
          </>
        ) : (
          <>
            <div className={styles.buttonsGrid}>
              <Skeleton
                containerClassName={cls(styles.placeholderParent, styles.placeholderFullWidth)}
                height="100%"
                width="100%"
              />
            </div>
            <HorizontalDivider />
          </>
        )}
        <div className={styles.buttonsGrid}>
          {gribble ? (
            <>
              <DropdownAnchor>
                <GribbleShareDropdown
                  gribble={{ id: Number(gribble.id) }}
                  text={shareText}
                  align="left"
                />
              </DropdownAnchor>
              <ViewOn
                openSea={externalRoutes.viewLinks.openSea.eggs(Number(gribble.id))}
                coinbase={externalRoutes.viewLinks.coinbaseNft.eggs(Number(gribble.id))}
                looksRare={externalRoutes.viewLinks.looksRare.eggs(Number(gribble.id))}
              />
              {/* disabled until release this is ready */}
              {/* <Button xsmall className={cls(styles.button)} disabled>
                Download Assets
              </Button>
              <Switch
                noColor={true}
                isSquare={true}
                items={[
                  { id: '2d', children: '2D' },
                  { id: '3d', children: '3D' },
                ]}
                initialSelection={'2d'}
                onChange={(item) => {
                  return item.id;
                }}
              /> */}
            </>
          ) : (
            <>
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
              <Skeleton containerClassName={styles.placeholderParent} height="100%" />
            </>
          )}
        </div>
        {gribble && !gribble.rendered && (
          <RenderingWarning hatchStartedAt={gribble.hatchStartedAt} />
        )}
        <div className={styles.image}>
          {gribble && gribble.pairedBreaker && (
            <img
              src={gribble.pairedBreaker?.breakerSrc}
              alt="Paired Breaker"
              className={styles.pairedBreaker}
            />
          )}
          {gribble ? (
            <img src={gribble.image?.full} className={styles.fullWidthImage} alt="Gribble Egg" />
          ) : (
            loadingImage
          )}
        </div>
      </div>
      <Traits properties={properties} />
    </div>
  );
}

export const CollectionSidebar = () => {
  const { selected, filter, setPairingModalOpen } = useCollectionContext();
  const { isLoading } = useCollection(filter.type);
  const metadata = useCollectionEntityMetadata(selected);

  const { unpair, pairs } = useEggBreakerPairs();
  const user = useUser();
  const userId = user.account.dusktech?.userId;

  const unpairEggOrGribble = () => {
    if (selected?.type === 'duskbreakers') {
      return;
    }
    const pair = pairs.find((pair) => pair.gribbleId === selected?.item.id);
    if (pair && userId) {
      unpair(userId, pair.id);
    }
  };

  const attributes = (
    metadata.status === 'loading'
      ? { type: 'loading' }
      : metadata.status === 'error'
      ? { type: 'error', message: metadata.error }
      : {
          type: 'success',
          data:
            metadata.data?.attributes?.map((x) =>
              traitAttributesTransformer.toTraitAttribute(x, selected?.type!),
            ) ?? [],
        }
  ) as IndeterminateProperties;

  const selection = isLoading ? filter.type : selected?.type;

  const ownsNoEntities = !isLoading && !selected;
  if (ownsNoEntities) {
    return null;
  }

  // When first fetching the collection we want to use the currently
  // selected filter to render the correct loading skeleton
  switch (selection) {
    case CollectionEntities.Gribbles:
      return (
        <SidebarGribble
          gribble={
            sidebarEntityTransformer.toSidebarEntity('gribbles', metadata.data) as
              | CollectionGribble
              | undefined
          }
          properties={attributes}
          setPairingModalOpen={() => setPairingModalOpen?.(true)}
          unpairGribble={unpairEggOrGribble}
        />
      );
    case CollectionEntities.Eggs:
      return (
        <SidebarEgg
          egg={
            sidebarEntityTransformer.toSidebarEntity('eggs', metadata.data) as
              | CollectionEgg
              | undefined
          }
          setPairingModalOpen={() => setPairingModalOpen?.(true)}
          unpairEgg={unpairEggOrGribble}
        />
      );
    case CollectionEntities.Duskbreakers:
      return (
        <SidebarBreaker
          breaker={selected?.item as CollectionBreaker | undefined}
          properties={attributes}
        />
      );
    // we can't know what entity type the client will receive and select
    // when filter.type = all & its the first load
    // so we default to a gribble simply because it looks awesome 😎
    default:
      return <SidebarGribble properties={{ type: 'loading' }} />;
  }
};

const sidebarEntityTransformer = {
  toSidebarEntity(
    entityType: CollectionEntityType,
    entity?: EntityMetadata,
  ): CollectionEgg | CollectionGribble | undefined {
    if (!entity) return;

    let pairedBreaker;
    if (entity.pairedBreakers?.[0]) {
      pairedBreaker = {
        breakerSrc: entity.pairedBreakers[0].imageUrl,
        paired: entity.pairedBreakers[0].tokenId !== undefined,
      };
    }
    const transformedEntity = {
      ...entity,
      id: entity.tokenId.value,
      image: {
        thumbnail: entity.imageUrl,
        full: entity.imageUrl,
      },
      pairedBreaker,
    };
    switch (entityType) {
      case 'eggs': {
        return { ...transformedEntity, level: entity.level } as CollectionEgg;
      }
      case 'gribbles': {
        return { ...transformedEntity, hatchStartedAt: new Date(entity.hatchStartedAt!) };
      }
      default: {
        throw Error(`${entityType} is not a valid collection type for this transformer`);
      }
    }
  },
};
